import { ICampaignV2Api } from './campaign-v2.api'
import {
   CampaignV2Error,
   IAssistant,
   IAvailableNumber,
   ICatalogItem,
   IConfigurationRead,
   IContentReference,
   IContentRevision,
   ICriteria,
   IInventoryNumber,
   ILiveCampaign,
   IPhoneDefinition,
   IStrategyDefinition,
   TContentDefinition,
   TContentKind,
} from '../interfaces/campaign-v2.types'
import { criteriaValues, localReadiness } from '../utils/config'

/**
 * The same API, answered from memory: used by `?dev=1` free flow and the
 * no-sign-in sandbox so the editor can be walked without the backend and
 * without creating real campaigns. Shapes follow the handoff examples.
 */

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))
let seq = 1000
const nextId = () => (seq += 1)
const uuid = () => `${Date.now().toString(36)}-${(seq += 1).toString(36)}`

export const DEMO_CATEGORIES = [
   { id: 4, name: 'Buyer' },
   { id: 5, name: 'Seller' },
   { id: 6, name: 'Lending' },
   { id: 7, name: 'Rental' },
]

interface IStoredContent {
   contentId: number
   kind: TContentKind
   shared: boolean
   revisions: IContentRevision[]
   publishedRevisionId: string
}

interface IStoredCampaign {
   read: IConfigurationRead
   live: ILiveCampaign
   reserved: string[]
}

interface IDemoState {
   content: Record<number, IStoredContent>
   campaigns: Record<string, IStoredCampaign>
   inventory: IInventoryNumber[]
}

const KEY = 'ff-campaign-v2-demo'

const seedContent = (): Record<number, IStoredContent> => {
   const out: Record<number, IStoredContent> = {}
   const put = (
      contentId: number,
      kind: TContentKind,
      definition: TContentDefinition,
      shared = true
   ) => {
      const rev: IContentRevision = {
         id: `rev-${contentId}-1`,
         contentId,
         kind,
         definition,
      }
      out[contentId] = {
         contentId,
         kind,
         shared,
         revisions: [rev],
         publishedRevisionId: rev.id,
      }
   }
   DEMO_CATEGORIES.forEach((c, i) => {
      const b = 100 * (i + 1)
      put(b + 1, 'phone', {
         name: `${c.name} first call`,
         businessTypeId: c.id,
         callType: 'outbound',
         callDirections: ['outbound'],
         leadTypes: ['known'],
         firstMessage: `Hi, this is {{assistant_name}} with {{company}}. Is now a convenient time to talk about your ${c.name.toLowerCase()} plans?`,
         sections: [
            {
               sectionId: 's1',
               name: 'Discovery',
               type: 'call_outbound',
               order: 1,
               content:
                  'Learn timeline, area and budget. Ask one question at a time.',
            },
            {
               sectionId: 's2',
               name: 'Book',
               type: 'call_outbound',
               order: 2,
               content:
                  'Offer two appointment times and confirm with the calendar tool.',
               protected: true,
            },
         ],
      })
      put(b + 2, 'phone', {
         name: `${c.name} incoming call`,
         businessTypeId: c.id,
         callType: 'inbound',
         callDirections: ['inbound'],
         leadTypes: ['known', 'unknown'],
         firstMessage: `Thanks for calling {{company}}, this is {{assistant_name}}. How can I help?`,
         sections: [
            {
               sectionId: 's1',
               name: 'Greeting',
               type: 'call_inbound',
               order: 1,
               content: 'Identify the caller, then help with their request.',
            },
         ],
      })
      put(b + 3, 'strategy', {
         name: `${c.name} 5-touch follow-up`,
         businessTypeId: c.id,
         sms: [
            {
               id: b + 31,
               template: `Hi {{first_name}}, it's {{assistant_name}} with {{company}}. Still looking? Happy to help.`,
            },
            {
               id: b + 32,
               template: `Would a quick call or a text thread work better for you?`,
            },
            {
               id: b + 33,
               template: `No rush. Reply anytime and I'll pick it up from there.`,
            },
         ],
         email: [
            {
               id: b + 34,
               subject: `Your ${c.name.toLowerCase()} plans`,
               body: `Reply with the areas and budget you have in mind and we will take it from there.`,
               contentType: 'text',
            },
         ],
         voicemail: [
            {
               id: b + 35,
               template: `Hi, this is {{assistant_name}} with {{company}}. Call us back when convenient.`,
            },
         ],
      })
      put(b + 4, 'conversation', {
         name: `${c.name} reply instructions`,
         businessTypeId: c.id,
         content:
            'Answer the latest message using the conversation context. Respect stop requests. Confirm appointments only after the calendar tool succeeds.',
      })
      put(b + 5, 'persona', {
         name: 'Warm and direct',
         businessTypeId: c.id,
         guidance: {
            personaGuidance: 'Be helpful and direct.',
            voiceStyle: null,
            speakingGuidance: 'Short natural sentences.',
            conversationStyle: 'One question at a time.',
         },
      })
   })
   return out
}

const seedInventory = (): IInventoryNumber[] => [
   {
      phoneNumber: '+17195550140',
      locality: 'Colorado Springs',
      region: 'CO',
      isUsedInCampaigns: false,
   },
   {
      phoneNumber: '+17195550177',
      locality: 'Colorado Springs',
      region: 'CO',
      isUsedInCampaigns: false,
   },
   {
      phoneNumber: '+13035550123',
      locality: 'Denver',
      region: 'CO',
      isUsedInCampaigns: true,
      campaignIds: ['legacy-1'],
   },
]

const load = (): IDemoState => {
   try {
      const raw = sessionStorage.getItem(KEY)
      if (raw) return JSON.parse(raw) as IDemoState
   } catch {
      // fall through
   }
   return { content: seedContent(), campaigns: {}, inventory: seedInventory() }
}

let state: IDemoState | null = null
const db = () => {
   if (!state) state = load()
   return state
}
const save = () => {
   try {
      sessionStorage.setItem(KEY, JSON.stringify(db()))
   } catch {
      // ignore
   }
}

/** Forget every demo campaign (dev bar "Restart"). */
export const resetDemoCampaigns = () => {
   state = { content: seedContent(), campaigns: {}, inventory: seedInventory() }
   save()
}

const DEMO_VOICES = [
   {
      voiceId: 'dakotah',
      name: 'Dakotah',
      description: 'Warm, unhurried, books the most appointments',
      previewUrl: null,
      avatar: null,
      gender: 'female',
   },
   {
      voiceId: 'bradward',
      name: 'Bradward',
      description: 'Clear and confident',
      previewUrl: null,
      avatar: null,
      gender: 'male',
   },
   {
      voiceId: 'aria',
      name: 'Aria',
      description: 'Bright and friendly',
      previewUrl: null,
      avatar: null,
      gender: 'female',
   },
]

const DEMO_AGENTS = [
   {
      crmUserId: 79,
      name: 'Sam Patel',
      email: 'sam@patelrealty.com',
      phone: '+17195550101',
   },
   {
      crmUserId: 78,
      name: 'Priya Nair',
      email: 'priya@patelrealty.com',
      phone: '+17195550102',
   },
   {
      crmUserId: 80,
      name: 'Lee Moreno',
      email: 'lee@patelrealty.com',
      phone: null,
   },
]

const DEMO_SOURCES: Record<string, number> = {
   Zillow: 1240,
   'Realtor.com': 860,
   Website: 410,
   Referral: 120,
}

const campaign = (id: string) => {
   const c = db().campaigns[id]
   if (!c) throw new CampaignV2Error('Campaign not found', 404, 'not_found')
   return c
}

const content = (contentId: number) => {
   const c = db().content[contentId]
   if (!c)
      throw new CampaignV2Error('Content not found', 404, 'content_not_found')
   return c
}

const revision = (c: IStoredContent, revisionId: string) =>
   c.revisions.find((r) => r.id === revisionId) ||
   c.revisions[c.revisions.length - 1]

const toItem = (c: IStoredContent): ICatalogItem => {
   const def = revision(c, c.publishedRevisionId).definition
   const phone = def as IPhoneDefinition
   const strat = def as IStrategyDefinition
   return {
      contentId: c.contentId,
      kind: c.kind,
      name: def.name,
      businessTypeId: def.businessTypeId,
      shared: c.shared,
      callDirections: c.kind === 'phone' ? phone.callDirections : undefined,
      leadTypes: c.kind === 'phone' ? phone.leadTypes : undefined,
      callerTypes: c.kind === 'phone' ? phone.callerTypes || ['ai'] : undefined,
      counts:
         c.kind === 'strategy'
            ? {
                 sms: strat.sms?.length || 0,
                 email: strat.email?.length || 0,
                 voicemail: strat.voicemail?.length || 0,
              }
            : undefined,
      revisionId: c.publishedRevisionId,
   }
}

const addRevision = (
   c: IStoredContent,
   definition: TContentDefinition,
   publish: boolean
) => {
   const rev: IContentRevision = {
      id: `rev-${c.contentId}-${c.revisions.length + 1}`,
      contentId: c.contentId,
      kind: c.kind,
      definition,
   }
   c.revisions.push(rev)
   if (publish) c.publishedRevisionId = rev.id
   save()
   return rev
}

const createOwned = (kind: TContentKind, definition: TContentDefinition) => {
   const contentId = nextId()
   // Series items get ids like the backend assigns them (order = index + 1).
   const def = JSON.parse(JSON.stringify(definition)) as TContentDefinition
   if (kind === 'strategy') {
      const s = def as IStrategyDefinition
      s.sms = (s.sms || []).map((i) => ({ id: nextId(), template: i.template }))
      s.email = (s.email || []).map((i) => ({
         id: nextId(),
         subject: i.subject,
         body: i.body,
         contentType: i.contentType || 'text',
      }))
      s.voicemail = (s.voicemail || []).map((i) => ({
         id: nextId(),
         template: i.template,
      }))
   }
   if (kind === 'phone') {
      const p = def as IPhoneDefinition
      p.sections = (p.sections || []).map((sec, i) => ({
         ...sec,
         sectionId: sec.sectionId || `s${i + 1}`,
         order: i + 1,
      }))
   }
   const rev: IContentRevision = {
      id: `rev-${contentId}-1`,
      contentId,
      kind,
      definition: def,
   }
   db().content[contentId] = {
      contentId,
      kind,
      shared: false,
      revisions: [rev],
      publishedRevisionId: rev.id,
   }
   save()
   return rev
}

const mergeDefinition = (
   base: TContentDefinition,
   changes: Partial<TContentDefinition>
): TContentDefinition => ({ ...base, ...changes }) as TContentDefinition

const toLive = (id: string): ILiveCampaign => {
   const c = campaign(id)
   return {
      ...c.live,
      phoneNumbers: c.read.configuration.phoneNumbers || [],
      status: c.read.status,
      campaignName: c.read.name,
   }
}

export const demoCampaignV2Api: ICampaignV2Api = {
   readiness: async () => {
      await wait(120)
      return { enabled: true, ready: true, reasons: [] }
   },
   businessCategories: async () => {
      await wait(120)
      return DEMO_CATEGORIES
   },
   catalog: async (kind, categoryId) => {
      await wait(180)
      return Object.values(db().content)
         .filter((c) => c.kind === kind)
         .map(toItem)
         .filter((i) => i.businessTypeId === categoryId)
   },
   voices: async () => {
      await wait(120)
      return DEMO_VOICES
   },
   agents: async () => {
      await wait(120)
      return DEMO_AGENTS
   },
   orgAnswering: async () => ({ receiver: 'ai' }),
   estimatedLeads: async (criteria: ICriteria[]) => {
      await wait(150)
      const sources = criteria
         .filter((c) => c.field === 'source' && c.operator === 'IN')
         .flatMap(criteriaValues)
      const base = sources.length
         ? sources.reduce((n, s) => n + (DEMO_SOURCES[s] || 90), 0)
         : Object.values(DEMO_SOURCES).reduce((a, b) => a + b, 0)
      const narrowing = criteria.filter((c) => c.field !== 'source').length
      return Math.max(12, Math.round(base / (1 + narrowing * 0.6)))
   },

   acquireRevision: async (kind, contentId) => {
      await wait(120)
      const c = content(contentId)
      if (c.kind !== kind)
         throw new CampaignV2Error('Wrong content kind', 400, 'validation')
      return revision(c, c.publishedRevisionId)
   },
   readRevision: async (revisionId, contentId) => {
      await wait(100)
      return revision(content(Number(contentId)), revisionId)
   },
   createContent: async (kind, definition) => {
      await wait(220)
      if (!definition.name?.trim())
         throw new CampaignV2Error(
            'definition.name is required',
            400,
            'validation'
         )
      return createOwned(kind, definition)
   },
   customizeContent: async (reference, changes) => {
      await wait(220)
      const base = revision(content(Number(reference.id)), reference.revisionId)
      return createOwned(base.kind, mergeDefinition(base.definition, changes))
   },
   reviseContent: async (reference, changes) => {
      await wait(200)
      const c = content(Number(reference.id))
      if (c.shared)
         throw new CampaignV2Error(
            'Shared content is read-only',
            403,
            'shared_content_read_only'
         )
      const base = JSON.parse(
         JSON.stringify(revision(c, reference.revisionId).definition)
      ) as TContentDefinition
      const ch = changes as Record<string, unknown>
      if (typeof ch.name === 'string') base.name = ch.name
      if (c.kind === 'strategy') {
         const s = base as IStrategyDefinition
         const patch = (
            list: Array<{ id?: number }>,
            items?: Array<Record<string, unknown>>
         ) =>
            (items || []).forEach((it) => {
               const target = list.find((x) => x.id === it.id) as
                  Record<string, unknown> | undefined
               if (!target)
                  throw new CampaignV2Error(
                     `Unknown item ${it.id}`,
                     400,
                     'validation'
                  )
               Object.assign(target, it)
            })
         patch(s.sms, ch.sms as Array<Record<string, unknown>>)
         patch(s.email, ch.email as Array<Record<string, unknown>>)
         patch(s.voicemail, ch.voicemail as Array<Record<string, unknown>>)
      }
      if (c.kind === 'phone') {
         const p = base as IPhoneDefinition
         if (typeof ch.firstMessage === 'string')
            p.firstMessage = ch.firstMessage
         ;(
            (ch.sections as Array<{
               sectionId: string | number
               content: string
            }>) || []
         ).forEach((s) => {
            const sec = p.sections.find(
               (x) => String(x.sectionId) === String(s.sectionId)
            )
            if (!sec)
               throw new CampaignV2Error(
                  `Unknown section ${s.sectionId}`,
                  400,
                  'validation'
               )
            if (sec.protected)
               throw new CampaignV2Error(
                  'That section is protected',
                  400,
                  'protected_section'
               )
            sec.content = s.content
         })
      }
      if (c.kind === 'conversation' && typeof ch.content === 'string')
         (base as { content: string }).content = ch.content
      if (c.kind === 'persona' && ch.guidance)
         (base as { guidance: unknown }).guidance = ch.guidance
      return addRevision(c, base, false)
   },
   publishContent: async (reference, expectedRevisionId) => {
      await wait(160)
      const c = content(Number(reference.id))
      if (c.publishedRevisionId !== expectedRevisionId)
         throw new CampaignV2Error(
            'Someone published a newer wording. Reload and try again.',
            409,
            'revision_conflict'
         )
      c.publishedRevisionId = reference.revisionId
      save()
      return revision(c, reference.revisionId)
   },

   createDraft: async (name, configuration, idempotencyKey) => {
      await wait(300)
      const existing = Object.values(db().campaigns).find(
         (c) => c.live.id === idempotencyKey
      )
      if (existing) return existing.read
      const id = `v2-${uuid()}`
      const read: IConfigurationRead = {
         campaignId: id,
         name,
         configuration,
         revision: 1,
         status: 'draft',
         publishedVersionId: null,
         activeVersionId: null,
         hasUnpublishedChanges: true,
      }
      db().campaigns[id] = {
         read,
         live: {
            id,
            campaignName: name,
            status: 'draft',
            aiAssistants: [],
            phoneNumbers: [],
            agentAssignmentConfig: null,
         },
         reserved: [],
      }
      save()
      return read
   },
   readConfiguration: async (campaignId) => {
      await wait(120)
      return campaign(campaignId).read
   },
   saveConfiguration: async (
      campaignId,
      expectedRevision,
      configuration,
      name
   ) => {
      await wait(220)
      const c = campaign(campaignId)
      if (c.read.revision !== expectedRevision)
         throw new CampaignV2Error(
            'This campaign was changed elsewhere. Reload to see the latest.',
            409,
            'revision_conflict'
         )
      c.read = {
         ...c.read,
         configuration: {
            ...configuration,
            phoneNumbers:
               configuration.phoneNumbers ?? c.read.configuration.phoneNumbers,
         },
         name: name ?? c.read.name,
         revision: c.read.revision + 1,
         hasUnpublishedChanges: true,
      }
      c.live.agentAssignmentConfig = configuration.routing
      save()
      return c.read
   },
   readCampaign: async (campaignId) => {
      await wait(120)
      return toLive(campaignId)
   },
   preview: async (campaignId) => {
      await wait(200)
      const c = campaign(campaignId)
      const reasons = localReadiness(
         c.read.configuration,
         c.live.aiAssistants.length
      )
      const selected = c.read.configuration.phoneNumbers || []
      return {
         ready: reasons.length === 0,
         reasons,
         cadence: c.read.configuration.cadence,
         selectedPhoneNumbers: selected,
         existingPhoneNumberCount: selected.filter(
            (n) => !c.reserved.includes(n)
         ).length,
         numbersToPurchase: selected.filter((n) => c.reserved.includes(n))
            .length,
      }
   },
   publish: async (campaignId, expectedRevision) => {
      await wait(250)
      const c = campaign(campaignId)
      if (c.read.revision !== expectedRevision)
         throw new CampaignV2Error(
            'This campaign was changed elsewhere. Reload to see the latest.',
            409,
            'revision_conflict'
         )
      const versionId = `ver-${uuid()}`
      c.read = {
         ...c.read,
         publishedVersionId: versionId,
         hasUnpublishedChanges: false,
      }
      save()
      return { versionId }
   },
   activate: async (campaignId) => {
      await wait(600)
      const c = campaign(campaignId)
      const reasons = localReadiness(
         c.read.configuration,
         c.live.aiAssistants.length
      )
      if (reasons.length)
         throw new CampaignV2Error(
            `Not ready: ${reasons[0]}`,
            400,
            reasons[0].split(':')[0]
         )
      const versionId =
         c.read.publishedVersionId && !c.read.hasUnpublishedChanges
            ? c.read.publishedVersionId
            : `ver-${uuid()}`
      c.read = {
         ...c.read,
         publishedVersionId: versionId,
         activeVersionId: versionId,
         hasUnpublishedChanges: false,
         status: 'active',
      }
      c.reserved.forEach((n) =>
         db().inventory.push({
            phoneNumber: n,
            isUsedInCampaigns: true,
            campaignIds: [campaignId],
         })
      )
      c.reserved = []
      c.live.status = 'active'
      save()
      return { campaignId, versionId }
   },
   setStatus: async (campaignId, status) => {
      await wait(180)
      const c = campaign(campaignId)
      c.read = { ...c.read, status }
      c.live.status = status
      save()
   },

   addAssistant: async (campaignId, assistant) => {
      await wait(200)
      const c = campaign(campaignId)
      const a: IAssistant = { id: nextId(), ...assistant }
      c.live.aiAssistants.push(a)
      save()
      return a
   },
   updateAssistant: async (campaignId, assistantId, patch) => {
      await wait(150)
      const c = campaign(campaignId)
      const a = c.live.aiAssistants.find(
         (x) => String(x.id) === String(assistantId)
      )
      if (!a) throw new CampaignV2Error('Assistant not found', 404)
      Object.assign(a, patch)
      save()
      return a
   },
   removeAssistant: async (campaignId, assistantId) => {
      await wait(150)
      const c = campaign(campaignId)
      c.live.aiAssistants = c.live.aiAssistants.filter(
         (x) => String(x.id) !== String(assistantId)
      )
      save()
   },
   inventoryNumbers: async () => {
      await wait(150)
      return db().inventory
   },
   availableNumbers: async (search) => {
      await wait(250)
      const code = (search || '').replace(/\D/g, '').slice(0, 3) || '719'
      if (code === '000') return []
      return Array.from({ length: 5 }, (_, i) => ({
         phoneNumber: `+1${code}555${String(200 + i).padStart(4, '0')}`,
         locality:
            code === '303'
               ? 'Denver'
               : code === '719'
                 ? 'Colorado Springs'
                 : 'Local',
         region: 'CO',
         friendlyName: `(${code}) 555-${String(200 + i).padStart(4, '0')}`,
         capabilities: { voice: true, SMS: true, MMS: false },
      })) as IAvailableNumber[]
   },
   pricing: async (totalNumbers) => {
      await wait(100)
      return totalNumbers
         ? {
              currentPeriod: { amount: 1.15 * totalNumbers, currency: 'USD' },
              nextPeriod: { amount: 1.15 * totalNumbers, currency: 'USD' },
           }
         : null
   },
   reserveNumber: async (campaignId, number) => {
      await wait(200)
      const c = campaign(campaignId)
      const numbers = c.read.configuration.phoneNumbers || []
      if (!numbers.includes(number.phoneNumber)) {
         c.read.configuration = {
            ...c.read.configuration,
            phoneNumbers: [...numbers, number.phoneNumber],
         }
         c.read.revision += 1
         c.reserved.push(number.phoneNumber)
      }
      save()
   },
   assignNumbers: async (campaignId, phoneNumbers) => {
      await wait(200)
      const c = campaign(campaignId)
      const numbers = c.read.configuration.phoneNumbers || []
      c.read.configuration = {
         ...c.read.configuration,
         phoneNumbers: Array.from(new Set([...numbers, ...phoneNumbers])),
      }
      c.read.revision += 1
      save()
   },
   removeNumbers: async (campaignId, phoneNumbers) => {
      await wait(150)
      const c = campaign(campaignId)
      c.read.configuration = {
         ...c.read.configuration,
         phoneNumbers: (c.read.configuration.phoneNumbers || []).filter(
            (n) => !phoneNumbers.includes(n)
         ),
      }
      c.reserved = c.reserved.filter((n) => !phoneNumbers.includes(n))
      c.read.revision += 1
      save()
   },
}

/** Only the reference shape leaves this file; the editor never sees storage. */
export const demoReferenceFor = (rev: IContentRevision): IContentReference => ({
   id: String(rev.contentId),
   revisionId: rev.id,
})
